import { staircaseConfig, timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const kind = Math.random() < variant.nogoRatio ? 'nogo' : 'go';
    const glyph = kind === 'nogo' ? variant.nogoGlyph : variant.goGlyph;
    const clamped = Math.max(staircaseConfig.minValue, Math.min(staircaseConfig.maxValue, durationMs));
    return {
        trialIdx,
        durationMs: clamped,
        glyph,
        kind,
    };
}
export function createFreezeFrameAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function resolveWith(resp) {
        if (resolver) {
            const r = resolver;
            resolver = null;
            rejecter = null;
            r(resp);
        }
    }
    return {
        mount() {
            // Idempotent — rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        // Glyph s'éteint, on garde une fenêtre de tap (awaiting_response).
                        update({ subPhase: 'awaiting_response' });
                        timers.push(setTimeout(() => {
                            // Pas de tap durant la fenêtre — résolution no-tap.
                            if (resolver && state.subPhase === 'awaiting_response') {
                                const wasGo = stim.kind === 'go';
                                // Pas tapé : correct si NoGo, incorrect si Go.
                                const correct = !wasGo;
                                resolveWith({
                                    tapped: false,
                                    wasGo,
                                    correct,
                                    reactionTimeMs: stim.durationMs + timing.awaitingResponseMs,
                                });
                            }
                        }, timing.awaitingResponseMs));
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleTap() {
            // On accepte le tap pendant stimulus OU awaiting_response.
            if (state.subPhase !== 'stimulus' && state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const wasGo = stim.kind === 'go';
            // Tapé : correct si Go, incorrect si NoGo (commission error).
            const correct = wasGo;
            const rt = Date.now() - trialStart;
            resolveWith({
                tapped: true,
                wasGo,
                correct,
                reactionTimeMs: rt,
            });
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map